import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Upload, Check } from 'lucide-react';
import { trackFunnelStep2 } from '../../utils/analytics';

interface ResumeStepProps {
  fileName: string;
  resumeContent: string;
  uploadProgress: number;
  isLoading: boolean;
  onFileUpload: (file: File) => Promise<void>;
  onContinue: () => void;
  salary: number;
  setSalary: (v: number) => void;
  experience: number;
  setExperience: (v: number) => void;
}

type CalendlyFakeCalendarProps = {
  onBack: () => void;
  onContinue: () => void;
  interviewStartDate: string;
  setInterviewStartDate: (v: string) => void;
  interviewEndDate: string;
  setInterviewEndDate: (v: string) => void;
  interviewTime: string;
  setInterviewTime: (v: string) => void;
};

export default function ResumeStep({
  fileName,
  resumeContent,
  uploadProgress,
  isLoading,
  onFileUpload,
  onContinue,
  salary,
  setSalary,
  experience,
  setExperience
}: ResumeStepProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia',
    'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
    'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming'
  ];
  const industries = [
    { id: 'tech', name: 'Technology' },
    { id: 'finance', name: 'Finance' },
    { id: 'healthcare', name: 'Healthcare' },
    { id: 'marketing', name: 'Marketing' },
    { id: 'education', name: 'Education' },
    { id: 'other', name: 'Other' }
  ];

  const [step, setStep] = React.useState(1);
  const [industry, setIndustry] = React.useState('');
  const [region, setRegion] = React.useState('');
  const [interviewStartDate, setInterviewStartDate] = React.useState('');
  const [interviewEndDate, setInterviewEndDate] = React.useState('');
  const [interviewTime, setInterviewTime] = React.useState('');

  React.useEffect(() => {
    // Track page view when component mounts
    trackFunnelStep2.viewResumeUpload();
  }, []);

  // Сохраняем выбранные даты и время в localStorage при изменении
  React.useEffect(() => {
    if (interviewStartDate) {
      localStorage.setItem('interviewStartDate', interviewStartDate);
    }
    if (interviewEndDate) {
      localStorage.setItem('interviewEndDate', interviewEndDate);
    }
    if (interviewTime) {
      localStorage.setItem('interviewTime', interviewTime);
    }
  }, [interviewStartDate, interviewEndDate, interviewTime]);

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      try {
        await onFileUpload(file);
        trackFunnelStep2.uploadResume(file.name, file.size);
      } catch (error) {
        console.error('Error uploading file:', error);
        trackFunnelStep2.uploadError(error instanceof Error ? error.message : 'Unknown error');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleContinue = () => {
    trackFunnelStep2.continueAfterUpload();
    onContinue();
  };

  return (
    <div className="p-8 md:p-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {step === 1 && (
          <>
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 font-gabarito">
              Upload your resume
            </h2>
            <p className="text-gray-600 text-center mb-8">
              We'll analyze your resume to create a personalized application kit
            </p>
            <div className="max-w-lg mx-auto">
              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-12 text-center mb-6 transition-colors ${
                  fileName ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-primary'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      onFileUpload(file)
                        .then(() => trackFunnelStep2.uploadResume(file.name, file.size))
                        .catch(error => trackFunnelStep2.uploadError(error instanceof Error ? error.message : 'Unknown error'));
                    }
                  }}
                  accept=".pdf,.doc,.docx,.txt"
                  className="hidden"
                />
                {fileName ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="bg-green-100 p-2 rounded-full">
                      <Check className="h-6 w-6 text-green-600" />
                    </div>
                    <span className="font-medium text-green-600">{fileName}</span>
                  </div>
                ) : (
                  <>
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 mb-4">
                      <Upload className="h-6 w-6 text-primary" />
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      Drag & drop your resume here or <span className="text-primary">browse files</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Supported formats: PDF, DOC, DOCX, TXT
                    </p>
                  </>
                )}
              </div>
              {uploadProgress > 0 && (
                <div className="mb-6">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-primary h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    {uploadProgress < 100 ? 'Uploading...' : 'Upload complete!'}
                  </p>
                </div>
              )}
              <div className="flex justify-center">
                <button
                  onClick={() => setStep(2)}
                  disabled={!resumeContent}
                  className="inline-flex items-center px-8 py-3 text-lg font-medium text-white bg-purple-gradient rounded-full hover:bg-purple-gradient-hover transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 font-gabarito">
              Tell us about your background
            </h2>
            <p className="text-gray-600 text-center mb-8">
              This helps us match you with the best opportunities
            </p>
            <div className="max-w-lg mx-auto">
              {/* Industry Dropdown */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                <select
                  value={industry}
                  onChange={e => setIndustry(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="" disabled>Select industry</option>
                  {industries.map(ind => (
                    <option key={ind.id} value={ind.id}>{ind.name}</option>
                  ))}
                </select>
              </div>
              {/* Region Dropdown */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Region (US State)</label>
                <select
                  value={region}
                  onChange={e => setRegion(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="" disabled>Select state</option>
                  {usStates.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              {/* Salary Slider */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Expected Salary (USD/year)</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min={50000}
                    max={500000}
                    step={1000}
                    value={salary}
                    onChange={e => setSalary(Number(e.target.value))}
                    className="w-full"
                  />
                  <span className="font-semibold text-gray-800 min-w-[90px] text-right">${salary.toLocaleString()}</span>
                </div>
              </div>
              {/* Experience Slider */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min={0}
                    max={20}
                    step={1}
                    value={experience}
                    onChange={e => setExperience(Number(e.target.value))}
                    className="w-full"
                  />
                  <span className="font-semibold text-gray-800 min-w-[40px] text-right">{experience} yr</span>
                </div>
              </div>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center px-6 py-3 text-lg font-medium text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-all duration-300 shadow"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!industry || !region}
                  className="inline-flex items-center px-8 py-3 text-lg font-medium text-white bg-purple-gradient rounded-full hover:bg-purple-gradient-hover transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
        {step === 3 && (
          <CalendlyFakeCalendar
            onBack={() => setStep(2)}
            onContinue={handleContinue}
            interviewStartDate={interviewStartDate}
            setInterviewStartDate={setInterviewStartDate}
            interviewEndDate={interviewEndDate}
            setInterviewEndDate={setInterviewEndDate}
            interviewTime={interviewTime}
            setInterviewTime={setInterviewTime}
          />
        )}
      </motion.div>
    </div>
  );
}

function CalendlyFakeCalendar({ onBack, onContinue, interviewStartDate, setInterviewStartDate, interviewEndDate, setInterviewEndDate, interviewTime, setInterviewTime }: CalendlyFakeCalendarProps) {
  // Фейковый календарь в стиле Calendly с выбором месяца и времени дня
  const [monthOffset, setMonthOffset] = React.useState(0);
  const today = new Date();
  const currentMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfWeek = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i + 1));
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const timeOptions = [
    { value: 'morning', label: 'Morning (8:00–11:00)' },
    { value: 'afternoon', label: 'Afternoon (12:00–16:00)' },
    { value: 'evening', label: 'Evening (17:00–20:00)' }
  ];

  // Для корректного отображения дней недели (начало месяца)
  const blanks = Array.from({ length: firstDayOfWeek }, () => null);

  // Логика выбора диапазона дат
  const handleDayClick = (date: Date) => {
    const iso = date.toISOString().slice(0, 10);
    if (!interviewStartDate || (interviewStartDate && interviewEndDate)) {
      setInterviewStartDate(iso);
      setInterviewEndDate('');
    } else if (interviewStartDate && !interviewEndDate) {
      if (iso < interviewStartDate) {
        setInterviewStartDate(iso);
        setInterviewEndDate('');
      } else if (iso === interviewStartDate) {
        setInterviewStartDate(iso);
        setInterviewEndDate('');
      } else {
        setInterviewEndDate(iso);
      }
    }
  };

  const isInRange = (date: Date) => {
    if (!interviewStartDate || !interviewEndDate) return false;
    const d = date.toISOString().slice(0, 10);
    return d >= interviewStartDate && d <= interviewEndDate;
  };
  const isSelected = (date: Date) => {
    const d = date.toISOString().slice(0, 10);
    return d === interviewStartDate || d === interviewEndDate;
  };

  return (
    <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl p-8">
      <h2 className="text-2xl font-bold text-center mb-4">Book your interview slot</h2>
      <p className="text-gray-600 text-center mb-8">Choose a convenient date range and time for your first interview. You can reschedule later if needed.</p>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setMonthOffset(m => m - 1)}
            className="px-2 py-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30"
            disabled={monthOffset === 0}
          >
            &lt;
          </button>
          <span className="font-semibold text-lg">
            {currentMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button
            onClick={() => setMonthOffset(m => m + 1)}
            className="px-2 py-1 rounded hover:bg-gray-100 text-gray-500"
          >
            &gt;
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map(d => (
            <div key={d} className="text-xs text-gray-400 text-center font-semibold pb-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 mb-4">
          {blanks.map((_, i) => <div key={i} />)}
          {days.map((d, idx) => {
            const selected = isSelected(d);
            const inRange = isInRange(d);
            return (
              <button
                key={idx}
                onClick={() => handleDayClick(d)}
                className={`py-2 rounded-lg text-sm font-medium transition-all duration-200 w-full
                  ${selected ? 'bg-primary text-white' : inRange ? 'bg-primary/20 text-primary' : 'bg-gray-100 text-gray-700 hover:bg-primary/10'}`}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {timeOptions.map(t => (
            <button
              key={t.value}
              onClick={() => setInterviewTime(t.value)}
              disabled={!interviewStartDate || !interviewEndDate}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${interviewTime === t.value ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-primary/10'} ${(!interviewStartDate || !interviewEndDate) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-center gap-4 mt-8">
        <button
          onClick={onBack}
          className="inline-flex items-center px-6 py-3 text-lg font-medium text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-all duration-300 shadow"
        >
          Back
        </button>
        <button
          onClick={onContinue}
          disabled={!interviewStartDate || !interviewEndDate || !interviewTime}
          className="inline-flex items-center px-8 py-3 text-lg font-medium text-white bg-purple-gradient rounded-full hover:bg-purple-gradient-hover transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
        >
          Continue
          <ArrowRight className="ml-2 w-5 h-5" />
        </button>
      </div>
    </div>
  );
}